import axios from 'axios'
import { LICENSE_CONFIG } from './constants.js'
import { resolveSeatLicenseType } from './portalOrganization.js'

export const HIGH_SCHOOL_ORG_TYPE = 'High School'

/**
 * State partner pricing applies only when the org is a high school in a partner state.
 *
 * @param {{ state?: string, organizationTypes?: string[] } | null | undefined} org
 * @param {boolean} stateIsPartner
 */
export function qualifiesForStatePartnerPricing (org, stateIsPartner) {
  if (!stateIsPartner) return false
  if (!String(org?.state ?? '').trim()) return false
  if (!Array.isArray(org?.organizationTypes)) return false
  return org.organizationTypes.includes(HIGH_SCHOOL_ORG_TYPE)
}

export function resolveLicenseConfig (licenseType) {
  return LICENSE_CONFIG[licenseType] || LICENSE_CONFIG.Basic
}

/**
 * @param {string} licenseType
 * @param {boolean} qualifies
 */
export function getEffectiveSeatPrice (licenseType, qualifies) {
  const config = resolveLicenseConfig(licenseType)
  return qualifies ? config.statePartnerPrice : config.basePrice
}

/** List / pre-discount seat price (always base). */
export function getListSeatPrice (licenseType) {
  return getEffectiveSeatPrice(licenseType, false)
}

/**
 * State partner discount as a whole percent for billing payloads (e.g. 10 for 10%).
 * @param {string} licenseType
 */
export function getStatePartnerDiscountPercent (licenseType) {
  const fraction = Number(resolveLicenseConfig(licenseType).discountPercent) || 0
  if (fraction <= 0) return 0
  const pct = fraction <= 1 ? fraction * 100 : fraction
  return Number.isInteger(pct) ? pct : Number(pct.toFixed(2))
}

/** List (pre-discount) fee for Course Basic/Legacy → Course Premium upgrades. */
export const COURSE_PREMIUM_UPGRADE_LIST_FEE_PER_SEAT = 18.75

/** State partner fee for Course Basic/Legacy → Course Premium upgrades (20% off list). */
export const COURSE_PREMIUM_UPGRADE_PARTNER_FEE_PER_SEAT = 15

/** Whole-percent discount for course premium upgrades (18.75 → 15). */
export const COURSE_PREMIUM_UPGRADE_DISCOUNT_PERCENT = 20

/**
 * Auto-note appended to estimate/invoice notes when a State Partner discount is applied.
 * @param {number | string | null | undefined} percent whole percent (e.g. 20), or null/0 to omit
 */
export function buildStatePartnerDiscountNote(percent) {
  const pctStr = formatDiscountPercentForNote(percent)
  if (pctStr) {
    return `State Partner Discount (${pctStr}%): This discount is applied for being a State Partner.`
  }
  return 'State Partner Discount: This discount is applied for being a State Partner.'
}

/**
 * @param {number | string | null | undefined} percent
 */
function formatDiscountPercentForNote(percent) {
  const p = Number(percent)
  if (!Number.isFinite(p) || p <= 0) return ''
  const display = p > 0 && p <= 1 ? p * 100 : p
  if (Number.isInteger(display)) return String(display)
  return display.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * @param {string | undefined | null} existingNotes
 * @param {string | undefined | null} extraNote
 */
export function mergeBillingNotes(existingNotes, extraNote) {
  const base = String(existingNotes ?? '').trim()
  const extra = String(extraNote ?? '').trim()
  if (!extra) return base
  if (!base) return extra
  if (base.includes(extra)) return base
  return `${base}\n\n${extra}`
}

/**
 * @param {string | undefined | null} fromLicenseType
 * @param {string | undefined | null} toLicenseType
 */
export function isCoursePremiumUpgrade (fromLicenseType, toLicenseType) {
  const from = resolveSeatLicenseType(fromLicenseType)
  const to = resolveSeatLicenseType(toLicenseType)
  return (from === 'Basic' || from === 'Legacy') && to === 'Premium'
}

/**
 * @param {boolean} qualifies
 */
export function getCoursePremiumUpgradeFeePerSeat (qualifies) {
  return qualifies
    ? COURSE_PREMIUM_UPGRADE_PARTNER_FEE_PER_SEAT
    : COURSE_PREMIUM_UPGRADE_LIST_FEE_PER_SEAT
}

/**
 * @param {string} fromLicenseType
 * @param {string} toLicenseType
 * @param {boolean} qualifies
 */
export function getConversionFeePerSeat (fromLicenseType, toLicenseType, qualifies) {
  const from = resolveSeatLicenseType(fromLicenseType)
  const to = resolveSeatLicenseType(toLicenseType)

  if (isCoursePremiumUpgrade(from, to)) {
    return getCoursePremiumUpgradeFeePerSeat(Boolean(qualifies))
  }

  const fromPrice = getEffectiveSeatPrice(from, qualifies)
  const toPrice = getEffectiveSeatPrice(to, qualifies)
  return Math.max(0, toPrice - fromPrice)
}

/**
 * Per-seat upgrade fee for in-progress seats on Premium / Supp. Premium orgs.
 *
 * @param {string} orgSeatLicenseType
 * @param {boolean} [qualifies]
 */
export function inProgressSeatUpgradeFeePerSeat (orgSeatLicenseType, qualifies = false) {
  const type = resolveSeatLicenseType(orgSeatLicenseType)
  if (type === 'Premium') {
    return getCoursePremiumUpgradeFeePerSeat(Boolean(qualifies))
  }
  if (type === 'Supp. Premium') {
    return getConversionFeePerSeat('Supp. Basic', 'Supp. Premium', Boolean(qualifies))
  }
  return 0
}

/**
 * @param {string | null | undefined} state Two-letter state abbreviation
 * @returns {Promise<boolean>}
 */
export async function fetchStatePortalPartnerStatus (state) {
  const abbrev = String(state ?? '').trim().toUpperCase()
  if (!abbrev || abbrev.length !== 2) return false
  try {
    const { data } = await axios.get(
      `${import.meta.env.VITE_BASE_LINK}/state-portal-partner-status/${encodeURIComponent(abbrev)}`
    )
    return data?.isStatePartner === true
  } catch (error) {
    console.error('[portal] fetchStatePortalPartnerStatus failed', error)
    return false
  }
}
